import type { TaskTitlePieSlice } from '../../utils/taskHelpers';

const DESKTOP = {
  viewW: 760,
  viewH: 460,
  radius: 150,
  labelGap: 42,
  labelWidth: 168,
} as const;

const STACKED = {
  viewW: 360,
  viewH: 360,
  radius: 138,
} as const;

const MIN_LABEL_PCT = 0;
const LINE_HEIGHT = 18;
const LABEL_GAP_Y = 7;

type LayoutSlice = TaskTitlePieSlice & {
  startAngle: number;
  endAngle: number;
  midAngle: number;
};

type LabelPlacement = {
  key: string;
  lines: string[];
  isRight: boolean;
  innerX: number;
  innerY: number;
  elbowX: number;
  elbowY: number;
  outerX: number;
  outerY: number;
  boxX: number;
  boxY: number;
  blockH: number;
};

function polar(cx: number, cy: number, radius: number, angleDeg: number) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return {
    x: cx + radius * Math.cos(rad),
    y: cy + radius * Math.sin(rad),
  };
}

function describeSlice(cx: number, cy: number, radius: number, startAngle: number, endAngle: number) {
  if (endAngle - startAngle >= 359.99) {
    return `M ${cx - radius} ${cy} A ${radius} ${radius} 0 1 1 ${cx + radius} ${cy} A ${radius} ${radius} 0 1 1 ${cx - radius} ${cy} Z`;
  }
  const start = polar(cx, cy, radius, endAngle);
  const end = polar(cx, cy, radius, startAngle);
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;
  return `M ${cx} ${cy} L ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArc} 0 ${end.x} ${end.y} Z`;
}

function layoutSlices(slices: TaskTitlePieSlice[]): LayoutSlice[] {
  let cursor = 0;
  return slices.map((slice) => {
    const sweep = (slice.pct / 100) * 360;
    const startAngle = cursor;
    const endAngle = cursor + sweep;
    cursor = endAngle;
    return {
      ...slice,
      startAngle,
      endAngle,
      midAngle: startAngle + sweep / 2,
    };
  });
}

function splitLabelLines(label: string): string[] {
  const upper = label.toUpperCase();
  if (upper.includes('/')) {
    return upper
      .split('/')
      .map((part) => part.trim())
      .filter(Boolean);
  }
  const words = upper.split(/\s+/).filter(Boolean);
  if (words.length <= 1 || upper.length <= 14) return [upper];
  const mid = Math.ceil(words.length / 2);
  return [words.slice(0, mid).join(' '), words.slice(mid).join(' ')];
}

function labelLines(label: string, count: number, pct: number): string[] {
  return [...splitLabelLines(label), `${count} · ${pct.toFixed(1)}%`];
}

function resolveSideCollisions(items: LabelPlacement[], viewH: number): void {
  items.sort((a, b) => a.boxY - b.boxY);
  for (let i = 1; i < items.length; i += 1) {
    const prev = items[i - 1]!;
    const cur = items[i]!;
    const minY = prev.boxY + prev.blockH + LABEL_GAP_Y;
    if (cur.boxY < minY) {
      cur.boxY = minY;
      cur.outerY = cur.boxY + cur.blockH / 2;
    }
  }

  const maxBottom = viewH - 4;
  for (let i = items.length - 1; i >= 0; i -= 1) {
    const cur = items[i]!;
    const bottom = cur.boxY + cur.blockH;
    if (bottom > maxBottom) {
      const shift = bottom - maxBottom;
      cur.boxY -= shift;
      cur.outerY -= shift;
      if (i > 0) {
        const prev = items[i - 1]!;
        const overlap = prev.boxY + prev.blockH + LABEL_GAP_Y - cur.boxY;
        if (overlap > 0) {
          prev.boxY -= overlap;
          prev.outerY -= overlap;
        }
      }
    }
    if (cur.boxY < 4) {
      cur.boxY = 4;
      cur.outerY = cur.boxY + cur.blockH / 2;
    }
  }
}

export type TaskTitlePieSvgMode = 'callouts' | 'stacked';

type TaskTitlePieSvgProps = {
  slices: TaskTitlePieSlice[];
  empty?: boolean;
  /** `stacked` = larger pie, no side labels (use HTML legend). */
  mode?: TaskTitlePieSvgMode;
};

/** Pie with leader-line callouts (desktop) or chart-only (mobile). */
export function TaskTitlePieSvg({ slices, empty = false, mode = 'callouts' }: TaskTitlePieSvgProps) {
  const stacked = mode === 'stacked';
  const viewW = stacked ? STACKED.viewW : DESKTOP.viewW;
  const viewH = stacked ? STACKED.viewH : DESKTOP.viewH;
  const radius = stacked ? STACKED.radius : DESKTOP.radius;
  const cx = viewW / 2;
  const cy = viewH / 2;
  const viewBox = `0 0 ${viewW} ${viewH}`;

  if (empty) {
    return (
      <svg className="task-pie__svg" viewBox={viewBox} role="img" aria-label="No tasks">
        <circle cx={cx} cy={cy} r={radius} className="task-pie__svg-empty" />
      </svg>
    );
  }

  const layout = layoutSlices(slices);

  if (stacked) {
    return (
      <svg className="task-pie__svg task-pie__svg--stacked" viewBox={viewBox} role="img" overflow="visible">
        <defs>
          <filter id="task-pie-shadow-stacked" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="2" stdDeviation="4" floodColor="#0f172a" floodOpacity="0.14" />
          </filter>
        </defs>
        <g className="task-pie__svg-pie" filter="url(#task-pie-shadow-stacked)">
          {layout.map((slice) => (
            <path
              key={slice.label}
              d={describeSlice(cx, cy, radius, slice.startAngle, slice.endAngle)}
              fill={slice.color}
              stroke="#ffffff"
              strokeWidth={2.5}
            >
              <title>{`${slice.label}: ${slice.count} (${slice.pct.toFixed(1)}%)`}</title>
            </path>
          ))}
        </g>
      </svg>
    );
  }

  const placements: LabelPlacement[] = [];

  for (const slice of layout) {
    if (slice.pct < MIN_LABEL_PCT) continue;
    const lines = labelLines(slice.label, slice.count, slice.pct);
    const blockH = lines.length * LINE_HEIGHT;
    const inner = polar(cx, cy, radius * 0.72, slice.midAngle);
    const elbow = polar(cx, cy, radius + 14, slice.midAngle);
    const rad = ((slice.midAngle - 90) * Math.PI) / 180;
    const isRight = Math.cos(rad) >= 0;
    const outerX = isRight ? cx + radius + DESKTOP.labelGap : cx - radius - DESKTOP.labelGap;
    const outerY = elbow.y;
    placements.push({
      key: slice.label,
      lines,
      isRight,
      innerX: inner.x,
      innerY: inner.y,
      elbowX: elbow.x,
      elbowY: elbow.y,
      outerX,
      outerY,
      boxX: isRight ? outerX + 6 : outerX - DESKTOP.labelWidth - 6,
      boxY: outerY - blockH / 2,
      blockH,
    });
  }

  resolveSideCollisions(
    placements.filter((p) => p.isRight),
    viewH,
  );
  resolveSideCollisions(
    placements.filter((p) => !p.isRight),
    viewH,
  );

  return (
    <svg className="task-pie__svg" viewBox={viewBox} role="img" overflow="visible">
      <defs>
        <filter id="task-pie-shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="2" stdDeviation="4" floodColor="#0f172a" floodOpacity="0.14" />
        </filter>
      </defs>

      <g className="task-pie__svg-pie" filter="url(#task-pie-shadow)">
        {layout.map((slice) => (
          <path
            key={slice.label}
            d={describeSlice(cx, cy, radius, slice.startAngle, slice.endAngle)}
            fill={slice.color}
            stroke="#ffffff"
            strokeWidth={2.5}
          >
            <title>{`${slice.label}: ${slice.count} (${slice.pct.toFixed(1)}%)`}</title>
          </path>
        ))}
      </g>

      {placements.map((p) => (
        <g key={`${p.key}-label`} className="task-pie__svg-label-group">
          <polyline
            className="task-pie__svg-leader"
            points={`${p.innerX},${p.innerY} ${p.elbowX},${p.elbowY} ${p.outerX},${p.outerY}`}
          />
          <foreignObject
            x={p.boxX}
            y={p.boxY}
            width={DESKTOP.labelWidth}
            height={p.blockH + 4}
            className="task-pie__svg-label-box"
          >
            <div
              className={`task-pie__svg-label-html${p.isRight ? '' : ' task-pie__svg-label-html--left'}`}
            >
              {p.lines.map((line, index) => (
                <span
                  key={`${p.key}-${index}`}
                  className={index === p.lines.length - 1 ? 'task-pie__svg-label-pct' : undefined}
                >
                  {line}
                </span>
              ))}
            </div>
          </foreignObject>
        </g>
      ))}
    </svg>
  );
}
